import {
  X, CheckCircle2, AlertTriangle, Loader2, ThumbsUp, ThumbsDown,
  MessageSquare, Hand, BellRing, ChevronRight, Zap
} from "lucide-react";

const INCIDENT_TYPES = [
  { id: "ACCESS_DENIED", label: "Access Denied", icon: require("lucide-react").Ban, color: "red" },
  { id: "DANGER_ROUTE", label: "Danger Route", icon: AlertTriangle, color: "red" },
  { id: "OBSTACLES", label: "Obstacles", icon: require("lucide-react").Construction, color: "amber" },
  { id: "MUD", label: "Mud", icon: require("lucide-react").Waves, color: "amber" },
  { id: "MACHINE_UNACCESSIBLE", label: "Machine Unaccessible", icon: require("lucide-react").Wrench, color: "red" },
];

export function FeedbackModal({ modal, onClose, mode, setMode, selectedIncident, setSelectedIncident, notes, setNotes, positiveMsg, setPositiveMsg, submitting, onSubmit }) {
  if (!modal) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-white/95 flex items-center justify-center p-4 animate-fadeIn" data-testid="feedback-modal">
      <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn max-h-[90vh] overflow-y-auto">
        <div className="bg-white p-5 flex justify-between items-center border-b border-slate-200 sticky top-0 z-10">
          <div>
            <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">Delivery Feedback</h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{modal.site_name}</p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-100 rounded-xl text-slate-400" data-testid="close-feedback-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!mode && (
            <div className="space-y-3" data-testid="feedback-mode-selector">
              <p className="text-xs text-slate-400 font-bold text-center uppercase tracking-wider">How did the delivery go?</p>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="feedback-positive-btn" onClick={() => setMode("positive")}
                  className="p-6 bg-lime-50 border-2 border-lime-300 rounded-xl flex flex-col items-center gap-3 hover:bg-lime-100 active:scale-95 transition-all">
                  <ThumbsUp className="w-10 h-10 text-lime-600" />
                  <span className="text-sm font-black text-lime-600 uppercase">All Good</span>
                </button>
                <button data-testid="feedback-negative-btn" onClick={() => setMode("negative")}
                  className="p-6 bg-red-500/10 border-2 border-red-500/30 rounded-xl flex flex-col items-center gap-3 hover:bg-red-500/20 active:scale-95 transition-all">
                  <ThumbsDown className="w-10 h-10 text-red-400" />
                  <span className="text-sm font-black text-red-400 uppercase">Problem</span>
                </button>
              </div>
            </div>
          )}
          {mode === "negative" && (
            <div className="space-y-4" data-testid="negative-feedback-panel">
              <p className="text-xs text-red-400 font-bold uppercase tracking-wider">Select Issue</p>
              <div className="grid grid-cols-1 gap-2">
                {INCIDENT_TYPES.map(inc => {
                  const Icon = inc.icon;
                  const isSelected = selectedIncident === inc.id;
                  return (
                    <button key={inc.id} data-testid={`incident-${inc.id}`} onClick={() => setSelectedIncident(inc.id)}
                      className={`w-full p-4 rounded-xl border flex items-center gap-3 transition-all active:scale-[0.98] ${
                        isSelected ? `border-${inc.color}-500 bg-${inc.color}-500/15 text-${inc.color}-400` : "border-slate-200 bg-white text-slate-400 hover:border-slate-200"
                      }`}>
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="text-sm font-bold uppercase tracking-tight">{inc.label}</span>
                      {isSelected && <CheckCircle2 className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Additional Details</label>
                <textarea data-testid="incident-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe the situation..."
                  className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 h-20 resize-none outline-none focus:border-red-500/40 placeholder:text-slate-400" />
              </div>
              <button data-testid="submit-incident-btn" onClick={onSubmit} disabled={!selectedIncident || submitting}
                className="w-full py-4 bg-red-600 text-white rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 disabled:opacity-30 active:scale-95 transition-all">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><AlertTriangle className="w-5 h-5" /> Report Incident</>}
              </button>
              <button onClick={() => setMode(null)} className="w-full py-2 text-xs text-slate-400 font-bold uppercase hover:text-slate-400">Back</button>
            </div>
          )}
          {mode === "positive" && (
            <div className="space-y-4" data-testid="positive-feedback-panel">
              <div className="p-5 bg-lime-50 border border-lime-300 rounded-xl text-center">
                <ThumbsUp className="w-10 h-10 text-lime-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-lime-600">Delivery went well!</p>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Add a message (optional)</label>
                <textarea data-testid="positive-message" value={positiveMsg} onChange={e => setPositiveMsg(e.target.value)} placeholder="Great site, smooth delivery..."
                  className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 h-16 resize-none outline-none focus:border-lime-400 placeholder:text-slate-400" />
              </div>
              <button data-testid="submit-positive-btn" onClick={onSubmit} disabled={submitting}
                className="w-full py-4 bg-lime-500 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 disabled:opacity-30 active:scale-95 transition-all">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ThumbsUp className="w-5 h-5" /> Send Feedback</>}
              </button>
              <button onClick={() => setMode(null)} className="w-full py-2 text-xs text-slate-400 font-bold uppercase hover:text-slate-400">Back</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DeliveryModal({ modal, onClose, ac, diesel, setDiesel, adblue, setAdblue, onConfirm, t }) {
  if (!modal) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-white/95 flex items-center justify-center p-4 animate-fadeIn" data-testid="delivery-modal">
      <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn">
        <div className="bg-white p-5 flex justify-between items-center border-b border-slate-200">
          <div>
            <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{t("confirm_delivery")}</h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-1">{modal.machine_number} / {modal.site_name}</p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-delivery-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">{ac.itemA} ({ac.itemAUnit})</label>
            <input data-testid="diesel-input" type="number" value={diesel} onChange={e => setDiesel(parseInt(e.target.value) || 0)}
              className="w-full p-4 bg-white border border-slate-200 rounded-xl text-xl font-black text-slate-900 font-mono text-center focus:border-lime-500 outline-none" />
          </div>
          {ac.itemBEnabled && (
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">{ac.itemB} ({ac.itemBUnit})</label>
              <input data-testid="adblue-input" type="number" value={adblue} onChange={e => setAdblue(parseInt(e.target.value) || 0)}
                className="w-full p-4 bg-white border border-slate-200 rounded-xl text-xl font-black text-slate-900 font-mono text-center focus:border-lime-500 outline-none" />
            </div>
          )}
          <button data-testid="confirm-delivery-btn" onClick={onConfirm}
            className="btn-industrial w-full py-4 bg-lime-500 text-slate-950 rounded-xl text-sm font-black shadow-md">
            <Zap className="w-5 h-5" /> {ac.deliveryLabel ? "Confirm" : t("deliver")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DriverSwitchModal({ show, onClose, drivers, currentName, onSwitch, t }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-white/95 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-slate-50 rounded-xl w-full max-w-sm border border-slate-200 animate-scaleIn">
        <div className="bg-white p-5 flex justify-between items-center border-b border-slate-200">
          <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{t("switch_driver")}</h3>
          <button onClick={onClose} className="p-2 bg-slate-100 rounded-xl text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {drivers.map(d => (
            <button key={d.driver_name} onClick={() => onSwitch(d.driver_name)}
              className={`w-full p-4 rounded-xl flex items-center justify-between border transition-all ${d.driver_name === currentName ? "bg-lime-50 border-lime-400 text-lime-600" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${d.driver_name === currentName ? "bg-lime-500 text-slate-950" : "bg-slate-700 text-white"}`}>
                  {d.driver_name[0]}
                </div>
                <div className="text-left">
                  <span className="text-sm font-bold uppercase tracking-tight block">{d.driver_name}</span>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{d.region}</span>
                </div>
              </div>
              {d.driver_name === currentName ? <CheckCircle2 className="w-5 h-5" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MessagesPanel({ show, onClose, messages, driverName, onMarkRead, driver, onHighFive, t }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[150] bg-white flex flex-col animate-slideInRight" data-testid="messages-panel">
      <header className="p-5 border-b border-slate-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-600 rounded-xl text-white"><MessageSquare className="w-5 h-5" /></div>
          <div>
            <h3 className="text-lg font-black uppercase tracking-tight text-slate-900">{t("messages")}</h3>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t("info_hub")}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2.5 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-messages"><X className="w-5 h-5" /></button>
      </header>
      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><BellRing className="w-3.5 h-3.5 text-lime-500" /> {t("messages")}</p>
          {messages.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-10 font-bold uppercase tracking-wider">{t("no_data")}</p>
          ) : messages.map(msg => (
            <div key={msg.id} onClick={() => !msg.read && msg.to_user === driverName && onMarkRead(msg.id)}
              className={`p-4 rounded-xl border transition-all ${msg.read || msg.from_user === driverName ? "bg-slate-50 border-slate-200 opacity-70" : "bg-lime-50 border-lime-300"}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">{msg.from_user}</span>
                <span className="text-[9px] font-mono text-slate-400">{new Date(msg.timestamp).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
              </div>
              <p className="text-sm font-bold text-slate-800">{msg.message}</p>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><Hand className="w-3.5 h-3.5 text-amber-500" /> {t("high_five_wall")}</p>
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">{t("my_rating")}</span>
                <div className="flex items-center gap-2 mt-1">
                  <Hand className="w-5 h-5 text-amber-500" />
                  <span className="text-2xl font-black text-slate-900">{driver?.high_five_count || 0}</span>
                </div>
              </div>
              <button onClick={() => onHighFive("Planner")} data-testid="give-planner-highfive"
                className="px-4 py-2 bg-amber-500 text-slate-950 rounded-xl text-xs font-bold uppercase flex items-center gap-2 active:scale-95 transition-all">
                <Hand className="w-3.5 h-3.5" /> {t("give_planner")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
